import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import StripePaymentForm from './StripePaymentForm';
import PaymentBreakdown from './PaymentBreakdown';

const PRESET_AMOUNTS = [25, 50, 100, 250];
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Preview only - mirrors server/utils/processingFees (2.9% + 30c, grossed up);
// the server's breakdown is what's actually charged.
const estimateCoveredFee = (amount) => {
  const cents = Math.round(Number(amount) * 100);
  if (!(cents > 0)) return 0;
  return (Math.ceil((cents + 30) / (1 - 0.029)) - cents) / 100;
};

const formatMoney = (value) => `$${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Public donation form for fundraiser events. No registration: the donor picks an
// amount and can optionally leave a name, company and email (for a receipt).
const FundraiserDonationForm = ({ token, fundraiser: initialFundraiser, payerCoversFees = true }) => {
  const [fundraiser, setFundraiser] = useState(initialFundraiser || { amountRaised: 0, donationCount: 0, goal: null });
  const [amount, setAmount] = useState('');
  const [donor, setDonor] = useState({ name: '', company: '', email: '' });
  const [message, setMessage] = useState('');
  const [coverFees, setCoverFees] = useState(payerCoversFees !== false);
  const [stripeConfig, setStripeConfig] = useState(null);
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [thankYou, setThankYou] = useState(false);

  const goal = Number(fundraiser.goal || 0);
  const raised = Number(fundraiser.amountRaised || 0);
  const percent = goal > 0 ? Math.min(100, Math.round((raised / goal) * 100)) : null;

  const startDonation = async () => {
    const value = Number(amount);
    if (!Number.isFinite(value) || value < 1) {
      toast.error('Please enter a donation amount of at least $1.');
      return;
    }
    if (donor.email.trim() && !EMAIL_REGEX.test(donor.email.trim())) {
      toast.error('Please enter a valid email address, or leave it blank.');
      return;
    }

    setIsProcessing(true);
    try {
      const res = await axios.post(`/api/public/donations/${encodeURIComponent(token)}/intent`, {
        amount: value,
        donor,
        message,
        coverFees
      });
      setStripeConfig({
        clientSecret: res.data.clientSecret,
        publishableKey: res.data.publishableKey,
        amount: res.data.amount,
        currency: res.data.currency || 'usd',
        breakdown: res.data.breakdown || null
      });
      setPaymentIntentId(res.data.paymentIntentId);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not start donation. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePaymentComplete = async (result) => {
    if (!result?.success || !result.paymentIntent) {
      if (result?.error) toast.error(result.error);
      return;
    }
    try {
      const res = await axios.post(`/api/public/donations/${encodeURIComponent(token)}/complete`, {
        paymentIntentId: result.paymentIntent.id || paymentIntentId
      });
      if (res.data?.fundraiser) setFundraiser(res.data.fundraiser);
    } catch (err) {
      // The charge went through; the Stripe webhook will still record the donation.
      console.error('[FundraiserDonationForm] Failed to confirm donation', err);
    }
    setStripeConfig(null);
    setThankYou(true);
  };

  const progress = (
    <div className="p-4 bg-pink-50 border border-pink-200 rounded-lg">
      <div className="flex items-baseline justify-between">
        <div className="text-2xl font-bold text-gray-900">{formatMoney(raised)}</div>
        <div className="text-sm text-gray-600">
          {goal > 0 ? `raised of ${formatMoney(goal)} goal` : 'raised so far'}
        </div>
      </div>
      {percent !== null && (
        <div className="mt-2 w-full h-2 bg-pink-100 rounded-full overflow-hidden">
          <div className="h-full bg-pink-500" style={{ width: `${percent}%` }} />
        </div>
      )}
      <div className="mt-2 text-xs text-gray-600">
        {fundraiser.donationCount || 0} donation{fundraiser.donationCount === 1 ? '' : 's'}
      </div>
    </div>
  );

  if (thankYou) {
    return (
      <div className="space-y-4">
        {progress}
        <div className="p-5 bg-green-50 border border-green-200 rounded-lg text-center">
          <h3 className="text-lg font-semibold text-green-900">Thank you for your donation!</h3>
          <p className="text-sm text-green-800 mt-1">
            {donor.email.trim() ? `A receipt is on its way to ${donor.email.trim()}.` : 'Your support means a lot.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {progress}

      {!stripeConfig && (
        <>
          <div>
            <div className="text-sm font-semibold text-gray-700 mb-2">Donation amount</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {PRESET_AMOUNTS.map(preset => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => setAmount(String(preset))}
                  className={`px-3 py-2 rounded-md border text-sm font-semibold transition ${
                    Number(amount) === preset
                      ? 'border-pink-500 bg-pink-50 text-pink-700'
                      : 'border-gray-200 text-gray-700 hover:border-pink-300'
                  }`}
                >
                  ${preset}
                </button>
              ))}
            </div>
            <div className="mt-2 relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                className="border rounded px-3 py-2 pl-7 w-full"
                type="number"
                min="1"
                step="1"
                inputMode="decimal"
                placeholder="Other amount"
                value={amount}
                onChange={e => setAmount(e.target.value)}
              />
            </div>
          </div>

          <div>
            <div className="text-sm font-semibold text-gray-700">Your info <span className="font-normal text-gray-500">(optional)</span></div>
            <p className="text-xs text-gray-500 mb-2">Leave these blank to donate anonymously.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input
                className="border rounded px-3 py-2"
                placeholder="Name"
                value={donor.name}
                onChange={e => setDonor(d => ({ ...d, name: e.target.value }))}
              />
              <input
                className="border rounded px-3 py-2"
                placeholder="Company / business"
                value={donor.company}
                onChange={e => setDonor(d => ({ ...d, company: e.target.value }))}
              />
              <input
                className="border rounded px-3 py-2 sm:col-span-2"
                type="email"
                placeholder="Email (for a receipt)"
                value={donor.email}
                onChange={e => setDonor(d => ({ ...d, email: e.target.value }))}
              />
              <textarea
                className="border rounded px-3 py-2 sm:col-span-2"
                rows={2}
                placeholder="Message (optional)"
                value={message}
                onChange={e => setMessage(e.target.value)}
              />
            </div>
          </div>

          <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={coverFees}
              onChange={e => setCoverFees(e.target.checked)}
            />
            <span>
              Cover the card processing fee
              {Number(amount) >= 1 ? ` (${formatMoney(estimateCoveredFee(amount))})` : ''} so 100% of my donation goes to the team
            </span>
          </label>

          <button
            onClick={startDonation}
            disabled={isProcessing}
            className="w-full sm:w-auto px-6 py-2 rounded-md bg-pink-600 text-white font-semibold hover:bg-pink-700 transition-colors disabled:opacity-60"
          >
            {isProcessing ? 'Starting…' : `Donate${Number(amount) >= 1 ? ` ${formatMoney(Number(amount) + (coverFees ? estimateCoveredFee(amount) : 0))}` : ''}`}
          </button>
        </>
      )}

      {stripeConfig && (
        <div>
          <div className="mb-4 flex items-center justify-between">
            <p className="text-sm text-gray-700">
              Donating <strong>{formatMoney(stripeConfig.amount / 100)}</strong>
            </p>
            <button
              type="button"
              onClick={() => setStripeConfig(null)}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Change
            </button>
          </div>
          <PaymentBreakdown breakdown={stripeConfig.breakdown} baseLabel="Donation" />
          <StripePaymentForm config={stripeConfig} onComplete={handlePaymentComplete} buttonLabel="Donate" />
        </div>
      )}
    </div>
  );
};

export default FundraiserDonationForm;
